<script setup lang="ts">
import { useTerminologyData, type TerminologyEntry } from '@/composables/useTerminologyData';
import { useI18n } from 'vue-i18n';
import { computed, nextTick, onMounted, ref, type Ref } from 'vue';
import playImg from '@/assets/images/play.svg';
import video1Img from '@/assets/images/Wie_melde_ich_mich_an.jpg';
import video2Img from '@/assets/images/Unterschiedliche_Sicherheitsstufen.jpg';
import video3Img from '@/assets/images/Kanton_Thurgau_Digitaler_Schalter_Intro_8.jpg';
import video4Img from '@/assets/images/Kanton_Thurgau_Digitaler_Schalter_Intro_6.jpg';
import video5Img from '@/assets/images/Kanton_Thurgau_Digitaler_Schalter_Intro_2.jpg';
import video6Img from '@/assets/images/Kanton_Thurgau_Digitaler_Schalter_Intro_3.jpg';
import { onBeforeRouteLeave } from 'vue-router';
import KpDropdown from '@kp/geofy-vuejs-lib/tg-system/components/KpDropdown.vue';

const terminology = [
    { index: 0, title: useI18n().t('faq_about_title'), data: useTerminologyData(['faq', 'about']) },
    { index: 1, title: useI18n().t('faq_planungsgeschaefte_title'), data: useTerminologyData(['faq', 'planungsgeschaefte']) },
    { index: 2, title: useI18n().t('faq_daten_title'), data: useTerminologyData(['faq', 'daten']) },
    { index: 3, title: useI18n().t('faq_unterstuetzung_title'), data: useTerminologyData(['faq', 'unterstuetzung']) },
    { index: 4, title: useI18n().t('faq_benachrichtigung_title'), data: useTerminologyData(['faq', 'benachrichtigung']) },
    { index: 5, title: useI18n().t('faq_account_title'), data: useTerminologyData(['faq', 'account']) },
    //{ index: 3, title: useI18n().t('faq_services_title'), data: useTerminologyData(['faq', 'services']) }
];
const actPage: Ref<number> = ref(0);

const actData = computed((): Ref<TerminologyEntry[]> => {
    for (let i = 0; i < terminology.length; i++) {
        if (terminology[i].index === actPage.value) {
            return terminology[i].data.data;
        }
    }
    return ref([]);
});

const videos = [
    {
        title: 'Anmeldung bei ePlan',
        url: 'https://youtu.be/yaH-S8T0l1E',
        description:
            'In diesem Video zeigen wir Ihnen, wie Sie schnell und einfach ein Benutzerkonto auf ePlan erstellen können.',
        img: video1Img
    },
    {
        title: 'Warum gibt es unterschiedliche Sicherheitsstufen?',
        url: 'https://www.youtube.com/watch?v=74zVGEOpwrQ',
        description:
            'Auf ePlan gibt es drei unterschiedliche Sicherheitsstufen. In diesem Video werden diese erklärt.&nbsp;',
        img: video2Img
    },
    {
        title: 'Wie nutze ich die Qualifizierte Digitale Signatur?',
        url: 'https://youtu.be/0H16TN7ub6U',
        description: 'Dieses Video erklärt, wie die qualifizierte elektronische Signatur genutzt wird.&nbsp; &nbsp;',
        img: video3Img
    },
    {
        title: 'Wie erlange ich die Sicherheitsstufe 2?',
        url: 'https://youtu.be/NsFinQ6aVc8',
        description:
            'In ePlan gibt es drei unterschiedliche Sicherheitsstufen. In diesem Video wird spezifisch darauf eingegangen, wie man die Sicherheitsstufe 2 erreicht.&nbsp;',
        img: video4Img
    },
    {
        title: 'Sind meine Daten sicher?',
        url: 'https://youtu.be/5mtyZanuFM4',
        description: 'Dieses Video gibt Auskunft, wie ernst uns die Sicherheit Ihrer Daten ist.',
        img: video5Img
    },
    {
        title: 'Was bedeutet medienbruchfreie Eingabe?',
        url: 'https://youtu.be/r71xyGK9q4k',
        description: 'In diesem Video erklären wir Ihnen, was unter einer medienbruchfreien Eingabe zu verstehen ist.',
        img: video6Img
    }
];

function navSelected(index: number) {
    actPage.value = index;
}

const largeAccordion = computed(() => windowWidth.value > 1056);

const windowWidth = ref(window.innerWidth);
onMounted(() => {
    nextTick(() => {
        window.addEventListener('resize', onResize);
    });
});

onBeforeRouteLeave(() => {
    window.removeEventListener('resize', onResize);
});

function onResize() {
    windowWidth.value = window.innerWidth;
}
</script>

<template>
    <section class="my-2_5rem ktg-main-container">
        <h2 class="ktg-title-02 mb-2rem">{{ $t('fragen_amp_antworten') }}</h2>
        <p class="ktg-body-lead" v-html="$t('faq_info_text')"></p>
    </section>
    <section class="videos">
        <div class="ktg-main-container">
            <h3 class="ktg-title-03 mb-2_5rem">{{ $t('faq_video_title') }}</h3>
            <div class="slider-items" id="video-slider-container">
                <ktg-card
                    v-for="video in videos"
                    :key="video.url"
                    appearance="white"
                    class="video-slider-card"
                    :href="video.url"
                    target="_blank"
                    :link-text="$t('zum_video')"
                    link-icon="long-arrow-right"
                >
                    <ktg-card-visual full="">
                        <div class="card-visual__wrapper">
                            <div class="card-visual__overlay">
                                <img :src="playImg" />
                            </div>
                            <div class="card-visual__image-frame-outer">
                                <div class="card-visual__image-frame-inner">
                                    <img class="card-visual__image" :src="video.img" alt="" />
                                </div>
                            </div>
                        </div>
                    </ktg-card-visual>
                    <ktg-card-eyebrow>
                        {{ $t('erklaervideo') }}
                    </ktg-card-eyebrow>
                    <ktg-card-title>{{ video.title }}</ktg-card-title>
                    <ktg-card-text
                        ><div class="ktg-body ktg-body-small" v-html="video.description"></div> </ktg-card-text
                ></ktg-card>
            </div>
            <ktg-scrollbar
                class="slider-scrollbar"
                orientation="horizontal"
                for="video-slider-container"
                appearance="blue"
            ></ktg-scrollbar>
        </div>
    </section>
    <section class="faq ktg-main-container">
        <h2 class="ktg-title-02 nav">{{ $t('haeufige_fragen') }}</h2>
        <ktg-vertical-navigation v-if="largeAccordion" class="faq-nav" content-title="Thema auswählen">
            <ktg-vertical-navigation-item
                v-for="data in terminology"
                :key="data.title"
                :selected="data.index === actPage"
                @click="navSelected(data.index)"
                icon="dropdown-arrow-right-small"
            >
                {{ data.title }}
            </ktg-vertical-navigation-item>
        </ktg-vertical-navigation>
        <ktg-field v-if="!largeAccordion">
            <ktg-label for="thema_chooser">{{ $t('choose_thema') }}</ktg-label>
            <KpDropdown
                appearance="grey"
                class="faq-nav w-full"
                v-model="actPage"
                :options="terminology"
                display-field="title"
                value-field="index"
                name="thema_chooser"
                :placeholder="$t('please_chose')"
            ></KpDropdown>
        </ktg-field>
        <div class="content">
            <ktg-accordion class="accordion">
                <ktg-accordion-item
                    v-for="data in actData?.value || []"
                    :key="data.title"
                    :content-title="data.title"
                    :size="largeAccordion ? 'large' : 'small'"
                >
                    <span class="ktg-body" v-html="data.content"></span>
                </ktg-accordion-item>
            </ktg-accordion>
        </div>
    </section>
</template>

<style scoped>
.videos {
    background-color: var(--ktg-color-neutral-01);
    padding-top: 5rem;
    padding-bottom: 5rem;

    .slider-items {
        display: flex;
        gap: 1rem;
        overflow: auto;
        scrollbar-width: none;
    }

    .slider-scrollbar {
        margin-top: 2.5rem;
        width: 10rem;
    }

    /*.slider-items {
            display: grid;
            -moz-column-gap: 1rem;
            column-gap: 1rem;
            grid-auto-flow: column;
            grid-auto-columns: calc((100vw - 1rem + .5rem - 1rem) / 1.5);
            width: -moz-fit-content;
            width: fit-content;
            margin-left: .5rem;
            margin-right: .5rem;
            margin-bottom: 2.5rem;
    }

    .video-slider-card {
        scroll-snap-align: start;
        width: auto;
        height: 100%;
        transition: opacity .2s;
    }*/

    .card-visual__wrapper {
        background-color: var(--ktg-color-brand2-02);
        padding: 1.25rem 2.5rem;
        pointer-events: none;
        position: relative;
        width: 100%;
    }
    .card-visual__overlay {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        z-index: 1;
        display: flex;
        justify-content: center;
        align-items: center;
    }
    .card-visual__image-frame-outer {
        background-color: var(--ktg-color-brand2-03);
        padding: 10px;
        border-radius: 10px;
    }
    .card-visual__image-frame-inner {
        aspect-ratio: 16 / 9;
        background-color: var(--ktg-color-dark-contrast);
        border-radius: 2px;
        overflow: hidden;
    }
    .card-visual__image {
        width: 100%;
        height: 100%;
        -o-object-fit: cover;
        object-fit: cover;
        opacity: 0.3;
    }
}

.faq {
    gap: 1rem;
    display: grid;
    margin-top: 5rem;
    margin-bottom: 5rem;
    grid-template-areas:
        'title'
        'menu'
        'content';

    h2 {
        grid-area: title;
    }

    .faq-nav {
        grid-area: menu;
    }
    .content {
        grid-area: content;
    }
}

@media only screen and (min-width: 1056px) {
    .faq {
        gap: 2.5rem;
        grid-template-columns: 1fr 3fr 1fr;
        grid-template-areas:
            'title title title'
            'menu content .';
    }
}
</style>
